/**
 * @fileoverview Flagged Transactions workspace — shared UI types + helpers
 * @module jobs/phases/review/flaggedTransactions/flaggedShared
 *
 * Typed views over the `flaggedTransactions/publicQueries` payloads (which
 * use v.any() returns) plus the badge colour maps used across the master
 * table, transaction table, and review modal.
 */

import type { BadgeColor } from "@/ui/primitives/badge";
import type { Id } from "@cvx/_generated/dataModel";

export type FlaggedHeadKey =
  | "unfair_preferences"
  | "related_entity_preference"
  | "insolvent_trading"
  | "director_related"
  | "uncommercial"
  | "related_entity_uncommercial"
  | "unfair_loans"
  | "defeat_creditors"
  | "creditor_defeating_disposition"
  | "defeat_employee_entitlements"
  | "ppsa_vesting"
  | "circulating_security_interest"
  | "officer_security_interest"
  | "bankruptcy_undervalued_transfer"
  | "bankruptcy_preference"
  | "bankruptcy_transfer_to_defeat_creditors";

/**
 * How well a subjective element (s588FE(5) purpose, s596AB fault) is evidenced
 * on the material available — recorded per flag, separately from the quantum.
 */
export type PurposeEvidence =
  "documented" | "circumstantial" | "not_established" | "insolvency_inferred";

export type HeadStatus =
  "live" | "stale" | "updating" | "locked" | "blocked" | "pending";

export interface FlaggedHistoryEntryUI {
  timestamp: number;
  actor: string;
  action: string;
  reason: string;
  /** Present on practitioner-attributed entries (decision/note changes). */
  userId?: Id<"users">;
}

export interface ProposedChangeUI {
  after: Record<string, unknown>;
  reasoning: string;
  proposedAt: number;
}

export interface WorkspaceHeadUI {
  headKey: FlaggedHeadKey;
  name: string;
  shortLabel: string;
  provision: string;
  uiDescription: string;
  status: HeadStatus;
  outcome:
    | "flags_identified"
    | "no_flags"
    | "insufficient_data"
    | "blocked_missing_inputs"
    | null;
  headline: string | null;
  reasoning: string | null;
  flagCount: number;
  totalValue: number;
  decisions: {
    pending: number;
    accepted: number;
    rejected: number;
    acceptedValue: number;
    /** Sum of recoverable estimates across not-rejected active flags. */
    recoverableValue: number;
  };
  /**
   * Portion of this head's recoverable that the agent also flagged under
   * another head (via `parallelHeadFlags`) — surfaced so the practitioner can
   * see where the same dollars are counted across heads and avoid
   * double-counting. Null when no flags overlap.
   */
  overlap: {
    count: number;
    value: number;
    /** Short labels of the other heads that also capture these flags. */
    heads: string[];
  } | null;
  /**
   * Set only for heads whose provision requires a purpose or fault element the
   * practitioner assesses. Carries how much of the live recoverable has no
   * documented material behind that element, so the total is never read as
   * settled.
   */
  subjectiveElement: {
    label: string;
    provision: string;
    undocumentedCount: number;
    undocumentedValue: number;
  } | null;
  /**
   * Extended-reach head only (s588FE(5)): live flags carrying no parallel to a
   * preference or uncommercial head, so their s588FA / s588FB character has not
   * been assigned (MOR-1264). Null on every other head.
   */
  extendedReach: {
    uncharacterisedCount: number;
    uncharacterisedValue: number;
  } | null;
  confidence: number | null;
  locked: boolean;
  staleSince: number | null;
  proposedSummary: ProposedChangeUI | null;
  perDirector: Array<Record<string, unknown>> | null;
  lastRunAt: number | null;
  reviewWindow: {
    start: string | null;
    end: string | null;
    basisNote: string;
    blocked: boolean;
  };
  /** What the window anchors are; generic "Window start/end" on corporate heads. */
  windowLabels: { start: string; end: string };
  taskId: Id<"summarizationTasks"> | null;
  changeHistory: FlaggedHistoryEntryUI[];
}

/** Bankruptcy case anchors shown in the workspace header (MOR-1260). */
export interface BankruptcyCaseUI {
  petitionType: string | null;
  petitionTypeLabel: string;
  petitionPresentedDate: string | null;
  dateOfBankruptcy: string | null;
  commencement: string | null;
  /** s 115 citation, e.g. "s 115(2) item 3". */
  section: string | null;
  /** Plain-English derivation or the reason commencement is blocked. */
  note: string;
  blocked: boolean;
  /** Only an act of bankruptcy date is on file; petition type not recorded. */
  legacy: boolean;
  carriedOnBusiness: boolean | null;
}

export interface WorkspaceUI {
  caseLocked: boolean;
  /** Which head family the job runs (MOR-1254). */
  regime: "corporate" | "personal";
  /** Present on bankruptcy jobs only. */
  bankruptcy: BankruptcyCaseUI | null;
  insolvency: {
    date: string | null;
    basis: string;
    note: string;
    override: { date: string; reason: string } | null;
  };
  totals: {
    flagCount: number;
    totalValue: number;
    dedupedRecoverable: number;
    headsWithFlags: number;
  };
  heads: WorkspaceHeadUI[];
  master: {
    taskId: Id<"summarizationTasks"> | null;
    running: boolean;
    liveText: string;
  };
  caseHistory: FlaggedHistoryEntryUI[];
}

export interface FlagRowUI {
  flagId: string;
  headKey: FlaggedHeadKey;
  headLabel: string;
  transactionDate: string | null;
  /** True when the row aggregates multiple transactions (date shows "Various"). */
  isGrouped: boolean;
  counterpartyName: string;
  counterpartyRelationship: string | null;
  description: string;
  amount: number;
  quantum: number | null;
  banding: "large" | "medium" | "small";
  risk: "high" | "medium" | "low" | null;
  decision: "pending" | "accepted" | "rejected";
  decisionLocked: boolean;
  rowLocked: boolean;
  isManualFlag: boolean;
  hasProposed: boolean;
  needsReview: boolean;
  status: "active" | "superseded";
  directorName: string | null;
  valuationRequired: boolean;
  /**
   * Extended-reach flag with no recorded s588FA / s588FB character
   * (MOR-1264), regardless of parallel heads. Always false on other heads.
   */
  characterMissing: boolean;
  /** Underlying source entry (for cross-head "same event" clustering). */
  source: { table: string; id: string | null };
  /** Exact bank-entry ids this flag was built from. */
  linkedTransactionIds: string[];
}

export interface FlagNoteUI {
  id: Id<"flaggedFlagNotes">;
  userId: Id<"users">;
  content: string;
  createdAt: number;
}

/** Raw bank transaction underlying a flag (publicQueries.getFlag). */
export interface FlagTransactionUI {
  id: string;
  date: string | null;
  description: string;
  amount: number;
  direction: "debit" | "credit";
  documentId: string | null;
  documentFileName: string | null;
  /** Per-line decision for sum_of_lines flags; null when not splittable. */
  lineDecision: "pending" | "accepted" | "rejected" | null;
  /** Practitioner's supporting note on this line, if any. */
  lineNote: string | null;
  /** Who last saved the note + when (for attribution). */
  lineNoteBy: Id<"users"> | null;
  lineNoteAt: number | null;
}

/** Review-modal detail (publicQueries.getFlag — full row + head labels). */
export interface FlagDetailUI {
  headKey: FlaggedHeadKey;
  headLabel: string;
  provision: string;
  transactionDate?: string;
  counterpartyName: string;
  counterpartyRelationship?: string;
  description: string;
  amount: number;
  quantum?: number;
  banding: "large" | "medium" | "small";
  risk?: "high" | "medium" | "low";
  reasoning?: string;
  evidence: Array<{
    note: string;
    detail: string | null;
    documentId: string | null;
    documentFileName: string | null;
  }>;
  windowBasis?: string;
  insolvencyBasis?: string;
  /** Purpose / fault-element heads: the circumstances observed + what is missing. */
  purposeBasis?: string;
  /** What material is on the file bearing on that element. */
  purposeEvidence?: PurposeEvidence;
  /** Extended-reach head: the s588FA / s588FB test the agent made out (MOR-1264). */
  character?: "s588FA" | "s588FB";
  characterBasis?: string;
  parallelHeadFlags?: string[];
  defenceIndicators?: string[];
  valuationRequired?: boolean;
  recoveryNote?: string;
  directorName?: string;
  /** "sum_of_lines" flags support per-transaction agree/disagree. */
  amountModel: "sum_of_lines" | "derived";
  decision: "pending" | "accepted" | "rejected" | "partially_accepted";
  decisionLocked: boolean;
  rowLocked: boolean;
  isManualFlag: boolean;
  manualNotes?: string;
  proposed?: ProposedChangeUI;
  status: "active" | "superseded";
  changeHistory: FlaggedHistoryEntryUI[];
  sourceDocuments: Array<{ documentId: string; fileName: string }>;
  transactions: FlagTransactionUI[];
  parallelFlags: Array<{
    headKey: FlaggedHeadKey;
    label: string;
    flagId: string | null;
  }>;
  notes: FlagNoteUI[];
}

/** "director" → "Director", "related party (associated entity)" → "Related Party (Associated Entity)". */
export const titleCase = (s: string) =>
  s.replace(/\b[a-z]/g, (c) => c.toUpperCase());

/**
 * Strip raw Convex record ids that older agent runs pasted into prose
 * (e.g. "EFT sd77m25bjc2bjsd1bmy70nazhx88dfhz — $25,000..."). New runs put
 * ids in structured fields instead; this keeps legacy notes readable.
 */
export const stripRecordIds = (s: string) =>
  s
    .replace(/\b(?=[a-z0-9]*\d)[a-z][a-z0-9]{25,40}\b/g, "")
    .replace(/\(\s*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/^[\s—–-]+/, "")
    .trim();

/**
 * Agent runs sometimes record the ABSENCE of a defence in `defenceIndicators`
 * (e.g. "No s588GA safe-harbour engagement", "No s588H reasonable-grounds
 * defence apparent on current data"). Those negations should never trip the
 * "possible defence evidence" surface — only affirmative evidence pointing
 * TOWARDS a defence belongs there. This drops leading-negation strings so the
 * badge and list reflect genuine indicators. The head prompt steers new runs
 * to keep negatives out of the array; this guards legacy/stray data.
 */
const DEFENCE_NEGATION_RE =
  /^\s*(no\b|none\b|nil\b|not\b|without\b|absent\b|there (is|are) no\b)/i;

export const affirmativeDefenceIndicators = (
  indicators: ReadonlyArray<unknown> | undefined | null,
): string[] =>
  (indicators ?? []).flatMap((s) => {
    if (typeof s !== "string") return [];
    const trimmed = s.trim();
    if (trimmed === "" || DEFENCE_NEGATION_RE.test(trimmed)) return [];
    return [trimmed];
  });

/**
 * Decision-aware outcome chip: once transactions have been identified, the
 * review progress is the state the practitioner cares about, not the agent's
 * last assessment.
 *
 * Practitioner-facing copy says "transactions", never "flags" — the record is a
 * transaction put up for review, and "flag" reads as a finding of wrongdoing the
 * agent is not entitled to make. The data model keeps its `flag` names.
 */
export function outcomeDisplay(head: {
  outcome: WorkspaceHeadUI["outcome"];
  flagCount: number;
  decisions: { pending: number; accepted: number; rejected: number };
}): { label: string; color: BadgeColor; detail?: string } {
  const { decisions, flagCount, outcome } = head;
  const decided = decisions.accepted + decisions.rejected;
  if (flagCount > 0) {
    if (decisions.pending === 0) {
      return {
        label: "Reviewed",
        color: "emerald",
        detail: [
          decisions.accepted > 0 ? `${decisions.accepted} agreed` : null,
          decisions.rejected > 0 ? `${decisions.rejected} disagreed` : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    }
    if (decided > 0) {
      return {
        label: "In review",
        color: "amber",
        detail: `${decided} of ${flagCount} reviewed`,
      };
    }
    return { label: "Transactions identified", color: "amber" };
  }
  if (!outcome) return { label: "Not run", color: "gray" };
  return {
    label: OUTCOME_LABEL[outcome],
    color: outcome === "no_flags" ? "emerald" : "gray",
  };
}

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "2026-03-17" → "17 March 2026". Anything unparseable comes back as-is. */
export const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) {
    return `${Number(m[3])} ${MONTHS_LONG[Number(m[2]) - 1]} ${m[1]}`;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return `${parsed.getDate()} ${MONTHS_LONG[parsed.getMonth()]} ${parsed.getFullYear()}`;
};

/**
 * Agent text sometimes arrives with JSON escape sequences the model left in
 * the tool-call string (literal "\\n", "\\u2019", stray wrapping quotes).
 * Decode them so paragraph rendering works, then humanise any ISO dates so
 * the practitioner reads "17 March 2026" rather than "2026-03-17".
 */
export const agentText = (s: string) =>
  s
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex: string) =>
      String.fromCharCode(parseInt(hex, 16)),
    )
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, " ")
    .replace(/\\"/g, '"')
    .replace(/^"|"$/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\b\d{4}-\d{2}-\d{2}\b/g, (iso) => fmtDate(iso))
    .trim();

export const fmtMoney = (v: number) =>
  `$${Math.round(v).toLocaleString("en-AU")}`;

export const fmtDateTime = (ms?: number | null) => {
  if (!ms) return "—";
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS_LONG[d.getMonth()].slice(0, 3)} ${d.getFullYear()}, ${d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * Per-head statutory elements the UI has to be explicit about, so a claim is
 * never read as more (or less) settled than the law makes it.
 *
 * Two dimensions today:
 *  - `noInsolvencyElement` — liability does not require the company's
 *    insolvency to be proven, so the estimated insolvency date neither gates
 *    the claim nor floors its review window (s588FC, s588E and s95A do not
 *    apply). Mirrors `dateSensitive: false` in the backend
 *    `FLAGGED_HEAD_CONFIGS`.
 *  - `subjective` — the provision requires a purpose or fault element that the
 *    agent does not assess. It surfaces the transaction and the circumstances
 *    it observed (`purposeBasis`) plus what material is on the file
 *    (`purposeEvidence`); the practitioner makes the assessment. Mirrors
 *    `subjectiveElement` in the backend `FLAGGED_HEAD_CONFIGS`.
 *
 * A head can have both, one, or neither. s588FE(5) appears here only under
 * `subjective` — its window is unfloored, but the insolvent-transaction
 * element still applies.
 */
export interface HeadElements {
  /** Prose for the insolvency chip + modal callout when insolvency is not an element. */
  noInsolvencyElement?: string;
  subjective?: {
    /** Element name for the chip (e.g. "Purpose"). */
    label: string;
    provision: string;
    /**
     * Prose for the chip tooltip + modal callout. States what the provision
     * requires and whose job the assessment is — nothing further. Keep it to
     * the statutory words: propositions about the element's scope or
     * thresholds are interpretation, and the platform cites no authority for
     * them.
     */
    note: string;
  };
}

export const HEAD_ELEMENTS: Partial<Record<FlaggedHeadKey, HeadElements>> = {
  director_related: {
    noInsolvencyElement:
      "s588FDA has no insolvency element — liability does not depend on the company having been insolvent, and the 4-year window is never floored by the estimated insolvency date (s588FC, s588E and s95A do not apply). The case-wide date is shown for context only; changing it does not affect this claim.",
  },
  unfair_loans: {
    noInsolvencyElement:
      "s588FD has no insolvency element — whether interest or charges were extortionate is judged when the loan was made or varied. There is no look-back period and no insolvency floor (s588FC, s588E and s95A do not apply). The case-wide date is shown for context only; changing it does not affect this claim.",
  },
  defeat_creditors: {
    subjective: {
      label: "Purpose",
      provision: "s588FE(5)",
      note: "s588FE(5) requires a purpose of defeating, delaying or interfering with creditors' rights. The agent surfaces possible transactions, but does not analyse the purpose of the transactions. This needs to be undertaken by the practitioner with regard to the circumstances and evidence.",
    },
  },
  creditor_defeating_disposition: {
    noInsolvencyElement:
      "s588FE(6B) has no insolvency element for dispositions in this window — an external administration began within 12 months of every disposition inside it, which satisfies s588FE(6B) on its own, and the 12-month window is never floored by the estimated insolvency date (s588FC, s588E and s95A do not apply). The case-wide date is shown for context only; changing it does not affect this claim.",
  },
  circulating_security_interest: {
    noInsolvencyElement:
      's588FJ has no transaction-level insolvency element — "wound up in insolvency" characterises the winding up itself, and solvency immediately after creation is the s588FJ(3) defence for the secured party to prove. The window is anchored on the creation date and is never floored by the estimated insolvency date (s588FC, s588E and s95A do not apply). The case-wide date is shown for context only; changing it does not affect this claim.',
  },
  officer_security_interest: {
    noInsolvencyElement:
      "s588FP has no insolvency element — the section voids an officer-held security interest enforced within 6 months of its instrument without Court leave, self-executing and outside the s588FE scheme (s588FC, s588E and s95A do not apply). Solvency immediately before the relevant time matters only to the s588FP(4) leave limbs. The case-wide date is shown for context only; changing it does not affect this claim.",
  },
  ppsa_vesting: {
    noInsolvencyElement:
      "s588FL has no insolvency element — vesting turns purely on registration timing against the critical time, and the 6-month window is never floored by the estimated insolvency date (s588FC, s588E and s95A do not apply). The case-wide date is shown for context only; changing it does not affect this claim.",
  },
  defeat_employee_entitlements: {
    noInsolvencyElement:
      "s596AB has no insolvency element — the live question is the fault element, not insolvency, and there is no fixed look-back window to floor (s588FC, s588E and s95A do not apply). The case-wide date is shown for context only; changing it does not affect this claim.",
    subjective: {
      label: "Fault element",
      provision: "s596AB",
      note: "s596AB requires a fault element in relation to preventing, or significantly reducing, the recovery of employee entitlements. The agent surfaces possible transactions, but does not analyse the fault element. This needs to be undertaken by the practitioner with regard to the circumstances and evidence.",
    },
  },
  bankruptcy_undervalued_transfer: {
    noInsolvencyElement:
      "s 120 has no insolvency element for the trustee to prove. Solvency at the time of the transfer is a safe harbour the transferee must prove, and only for transfers more than 2 years (4 years for a related entity) before commencement. The case-wide date is shown for context only.",
  },
  bankruptcy_transfer_to_defeat_creditors: {
    subjective: {
      label: "Main purpose",
      provision: "s 121(1)(b)",
      note: "s 121 requires that the transferor's main purpose was to prevent, hinder or delay property becoming available to creditors. The agent surfaces possible transfers and records whether insolvency at the time can reasonably be inferred (s 121(2)), but the assessment is the practitioner's.",
    },
  },
};

/**
 * What material the agent found bearing on a subjective element. This rates the
 * MATERIAL, not the element: the agent does not assess whether the element is
 * made out, so the labels describe what is on the file and nothing more.
 */
export const PURPOSE_EVIDENCE_BADGE: Record<
  PurposeEvidence,
  { label: string; color: BadgeColor; detail: string }
> = {
  documented: {
    label: "Documented material",
    color: "emerald",
    detail:
      "Contemporaneous material is on the file (instructions, advice, minutes, correspondence).",
  },
  circumstantial: {
    label: "Circumstantial only",
    color: "amber",
    detail:
      "Only surrounding circumstances are available — timing, relationships, and where the value went.",
  },
  not_established: {
    label: "No supporting material",
    color: "red",
    detail: "Nothing on the file bears on this element.",
  },
  insolvency_inferred: {
    label: "Inferred from insolvency (s 121(2))",
    color: "amber",
    detail:
      "The main purpose is taken to be made out because insolvency at the time of the transfer can reasonably be inferred (Bankruptcy Act s 121(2)). The practitioner still weighs the s 121(4) defence.",
  },
};

/** Plain-English labels for the insolvency-date derivation basis. */
export const INSOLVENCY_BASIS_LABEL: Record<string, string> = {
  practitioner_override: "Practitioner override",
  books_records_inadequacy: "Books & Records inadequacy",
  financial_indicators: "Financial indicators",
  unavailable: "Not yet derived",
  unpaid_judgment: "Unpaid judgment",
  bankruptcy_notice: "Bankruptcy notice not complied with",
  statement_of_affairs: "Statement of affairs",
};

export const STATUS_BADGE: Record<
  HeadStatus,
  { label: string; color: BadgeColor }
> = {
  live: { label: "Live", color: "emerald" },
  stale: { label: "Stale", color: "amber" },
  updating: { label: "Updating", color: "blue" },
  locked: { label: "Locked", color: "violet" },
  blocked: { label: "Blocked", color: "red" },
  pending: { label: "Not run", color: "gray" },
};

const FALLBACK_BLOCKED_TOOLTIP =
  "This agent cannot run until a required input is available.";

/**
 * Hover copy for the Agent Status chip. Blocked rows surface the
 * review-window basis note (missing appointment date, insolvency date, etc.)
 * so the practitioner can see what to fix; other non-live states get a
 * short explanation of the run state.
 */
export function agentStatusTooltip(args: {
  status: HeadStatus;
  basisNote?: string;
}): string | null {
  switch (args.status) {
    case "blocked": {
      const note = args.basisNote?.trim();
      return note && note.length > 0 ? note : FALLBACK_BLOCKED_TOOLTIP;
    }
    case "pending":
      return "This agent has not run yet.";
    case "updating":
      return "The agent is currently running.";
    case "locked":
      return "This analysis is locked and will not re-run until it is unlocked.";
    case "stale":
      return "Inputs have changed since the last run. Re-run to refresh.";
    case "live":
      return null;
  }
}

/**
 * When a blocked head is waiting on appointment / relation-back details,
 * the tooltip offers a deep-link to Job Settings → Appointment Details.
 * Insolvency-date blocks and inapplicable appointment types are left without
 * a link — Settings cannot supply those.
 */
export function blockedSettingsFix(
  status: HeadStatus,
  basisNote?: string,
): { tab: "appointment"; label: string } | null {
  if (status !== "blocked") return null;
  const note = basisNote ?? "";
  if (/relation-back/i.test(note)) {
    return {
      tab: "appointment",
      label: "Set the relation-back date in Settings",
    };
  }
  if (
    /appointment date/i.test(note) ||
    /appointment details/i.test(note) ||
    /winding-up application/i.test(note) ||
    /court order date/i.test(note)
  ) {
    return {
      tab: "appointment",
      label: "Add appointment details in Settings",
    };
  }
  return null;
}

export const RISK_BADGE: Record<string, { label: string; color: BadgeColor }> =
  {
    high: { label: "High", color: "red" },
    medium: { label: "Medium", color: "amber" },
    low: { label: "Low", color: "gray" },
  };

export const DECISION_BADGE: Record<
  string,
  { label: string; color: BadgeColor }
> = {
  pending: { label: "Pending", color: "gray" },
  accepted: { label: "Agreed", color: "emerald" },
  rejected: { label: "Disagreed", color: "red" },
  partially_accepted: { label: "Partially Agreed", color: "amber" },
};

// ---------------------------------------------------------------------------
// Pursued against (respondent)
// ---------------------------------------------------------------------------

/**
 * Who a claim under each head is pursued against. The transaction's
 * counterparty is often NOT the respondent — an insolvent-trading claim is
 * against the director(s), not the supplier the debt was incurred to — so the
 * table derives a separate "pursued against" value per head:
 *
 *  - `counterparty` heads: the order is sought against the other party to the
 *    transaction (creditor preferred, transferee, lender, secured party).
 *  - `director` heads: liability attaches to the director / officer recorded
 *    on the flag (`directorName`). When the agent hasn't attributed one, the
 *    `fallback` label keeps the group honest ("Director(s) — unattributed")
 *    rather than silently falling back to the counterparty. Heads where the
 *    counterparty usually IS the director/associate (s588FDA, s588FP) fall
 *    back to the counterparty instead.
 */
export const HEAD_RESPONDENT: Record<
  FlaggedHeadKey,
  {
    source: "counterparty" | "director";
    /** Used when `source` is "director" but the flag has no directorName. */
    fallback?: string;
    /** Short statement of who the claim is pursued against (tooltips). */
    note: string;
  }
> = {
  unfair_preferences: {
    source: "counterparty",
    note: "s588FF order sought against the creditor who received the preference.",
  },
  related_entity_preference: {
    source: "counterparty",
    note: "s588FF order sought against the related-entity creditor who received the preference.",
  },
  insolvent_trading: {
    source: "director",
    fallback: "Director(s) — unattributed",
    note: "s588M compensation is pursued against the director(s) in office when the debt was incurred, not the creditor the debt is owed to.",
  },
  director_related: {
    source: "director",
    note: "s588FDA recovery is pursued against the director or close associate who received the benefit.",
  },
  uncommercial: {
    source: "counterparty",
    note: "s588FF order sought against the party who received value under the transaction.",
  },
  related_entity_uncommercial: {
    source: "counterparty",
    note: "s588FF order sought against the related-entity counterparty to the transaction.",
  },
  unfair_loans: {
    source: "counterparty",
    note: "s588FF order sought against the lender.",
  },
  defeat_creditors: {
    source: "counterparty",
    note: "s588FF order sought against the transferee / recipient of the transaction.",
  },
  creditor_defeating_disposition: {
    source: "counterparty",
    note: "s588FF order sought against the transferee / recipient of the disposition.",
  },
  defeat_employee_entitlements: {
    source: "director",
    fallback: "Director(s) — unattributed",
    note: "s596AC compensation is pursued against the person(s) who entered into the transaction — typically the director(s), not the transaction counterparty.",
  },
  ppsa_vesting: {
    source: "counterparty",
    note: "The security interest vests as against the secured party (s588FL).",
  },
  circulating_security_interest: {
    source: "counterparty",
    note: "s588FJ avoidance operates against the secured party.",
  },
  officer_security_interest: {
    source: "director",
    note: "s588FP voids the security as against the officer who holds it.",
  },
  bankruptcy_undervalued_transfer: {
    source: "counterparty",
    note: "The s 120 transfer is void against the trustee — recovery is pursued against the transferee who received the property.",
  },
  bankruptcy_preference: {
    source: "counterparty",
    note: "The s 122 preference is void against the trustee — recovery is pursued against the creditor who received it.",
  },
  bankruptcy_transfer_to_defeat_creditors: {
    source: "counterparty",
    note: "The s 121 transfer is void against the trustee — recovery is pursued against the transferee who received the property.",
  },
};

/**
 * Resolves who the claim on this row would be pursued against.
 *
 * Director-sourced heads use the flag's attributed director/officer; when the
 * head has no `fallback` (the counterparty is usually the director/associate
 * themselves, e.g. s588FDA payments made to the director) the counterparty
 * name stands in.
 */
export function pursuedAgainst(
  row: Pick<FlagRowUI, "headKey" | "counterpartyName"> & {
    directorName?: string | null;
  },
): string {
  const config = HEAD_RESPONDENT[row.headKey];
  if (config.source === "director") {
    const director = row.directorName?.trim();
    if (director) return director;
    return config.fallback ?? row.counterpartyName;
  }
  return row.counterpartyName;
}

/**
 * One distinct colour per voidable-transaction head, so the agent/claim a
 * flag belongs to is identifiable at a glance and consistent everywhere a
 * head badge renders (table Agent column, linked-event chips, etc.).
 */
export const HEAD_BADGE: Record<FlaggedHeadKey, BadgeColor> = {
  unfair_preferences: "blue",
  // gray is the only BadgeColor not already assigned to a head; the
  // blue-adjacent shades (sky, indigo, teal) are taken and BadgeColor is
  // kept in lock-step with the tasks palette (TaskColor), so widening it
  // would leak an unrelated task-picker option.
  related_entity_preference: "gray",
  insolvent_trading: "red",
  director_related: "violet",
  uncommercial: "amber",
  related_entity_uncommercial: "orange",
  unfair_loans: "teal",
  defeat_creditors: "pink",
  creditor_defeating_disposition: "sky",
  defeat_employee_entitlements: "indigo",
  ppsa_vesting: "green",
  circulating_security_interest: "purple",
  officer_security_interest: "emerald",
  bankruptcy_undervalued_transfer: "orange",
  bankruptcy_preference: "blue",
  bankruptcy_transfer_to_defeat_creditors: "pink",
};

export const OUTCOME_LABEL: Record<string, string> = {
  flags_identified: "Transactions identified",
  no_flags: "No transactions identified",
  insufficient_data: "Insufficient data",
  blocked_missing_inputs: "Blocked — missing inputs",
};
